import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  bronzeIconGradient,
  goldIconGradient,
  silverIconGradient,
} from '@sorare/core/src/atoms/icons/gradients.ts';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconAlert2 } from '@sorare/core/src/atoms/icons/system/iconAlert2.ts';
import { iconTrophy } from '@sorare/core/src/atoms/icons/system/iconTrophy.ts';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { LabelS } from '@sorare/core/src/atoms/typography/index.tsx';
import { OverlapItems } from '@sorare/core/src/atoms/ui/OverlapItems/index.tsx';
import { Avatar } from '@sorare/core/src/components/user/Avatar/index.tsx';
import { range } from '@sorare/core/src/lib/arrays.ts';

import type { SquadAvatars_squadStep } from './__generated__/index.graphql.ts';
import placeholderImg from './assets/placeholder.webp';

const Placeholder = styled(Horizontal).attrs({ center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  background-color: var(--c-nd-100-opaque);
  overflow: hidden;
  padding: var(--half-unit) 2px 0 2px;
`;
const Img = styled.img`
  width: calc(100% - var(--half-unit));
  height: calc(100% - var(--half-unit));
  object-fit: contain;
`;
const RelativeWrapper = styled.div`
  position: relative;
`;
const AbsoluteFontAwesomeIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 0;
  left: calc(-1 * var(--unit));
  width: var(--double-unit);
  height: var(--double-unit);
`;
const gradients = [goldIconGradient, silverIconGradient, bronzeIconGradient];

const PlaceHolderAvatar = () => {
  return (
    <Placeholder>
      <Img src={placeholderImg} alt="" />
    </Placeholder>
  );
};

type Props = { step: SquadAvatars_squadStep };
export const SquadAvatars = ({ step }: Props) => {
  const { squadLeaderboardLineups, minimumLineupsToStartStep } = step;

  if (!squadLeaderboardLineups.length) {
    return (
      <OverlapItems offset={12}>
        {range(minimumLineupsToStartStep).map(i => (
          <PlaceHolderAvatar key={i} />
        ))}
      </OverlapItems>
    );
  }

  const missingLineups =
    minimumLineupsToStartStep - squadLeaderboardLineups.length;
  if (missingLineups > 0) {
    return (
      <Vertical gap={0.5} center>
        <OverlapItems offset={12}>
          {squadLeaderboardLineups.map(({ lineup }) => (
            <Avatar key={lineup.id} user={lineup.user} size={4} rounded />
          ))}

          {range(missingLineups).map(i => (
            <PlaceHolderAvatar key={i} />
          ))}
        </OverlapItems>
        <Horizontal gap={0.5} center>
          <FontAwesomeIcon
            icon={iconAlert2}
            size="xs"
            color="var(--c-yellow-400)"
          />
          <LabelS color="var(--c-yellow-400)">
            <FormattedMessage
              defaultMessage="Need {count, plural, one {1 more player} other {{count} more players}}"
              values={{ count: missingLineups }}
            />
          </LabelS>
        </Horizontal>
      </Vertical>
    );
  }

  return (
    <Horizontal>
      {squadLeaderboardLineups
        .slice(0, minimumLineupsToStartStep)
        .map(({ lineup }, index) => {
          return (
            <RelativeWrapper key={lineup.id}>
              <Avatar user={lineup.user} size={4} rounded />
              <AbsoluteFontAwesomeIcon
                icon={iconTrophy}
                gradient={gradients.at(index)}
                size="xl"
              />
            </RelativeWrapper>
          );
        })}
    </Horizontal>
  );
};

SquadAvatars.fragments = {
  squadStep: gql`
    fragment SquadAvatars_squadStep on SquadStep {
      id
      minimumLineupsToStartStep
      squadLeaderboardLineups {
        id
        lineup {
          id
          user {
            slug
            ...Avatar_publicUserInfoInterface
          }
        }
      }
    }
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<SquadAvatars_squadStep>,
};
